import _ from 'lodash';

/**
 * Formats the category modules for use with select2 plugin
 *
 * @param   {array}  modules
 * @return  {object}
 */
const _mapCategoryModulesSelect2 = (modules, ui) => {
    return modules.map((module) => {
        return {
            id: module.id,
            text: module.name,
        };
    });
};

export const getModuleOptions = (ModuleConfigurator) => {
    const selectedModule = ModuleConfigurator.generator.selectedModule;

    /**
     * Field configuration (props passed to form components) Type of component is visible in the type property
     * (see comments in components to see what each of these properties is for)
     *
     * @type  {Array}
     */
    let configuration = [
        /**
         * Module type field
         */
        {
            disabled: !ModuleConfigurator.generator.newModule,
            id: 'type',
            label: 'Module Type',
            mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_TYPE',
            name: 'module[type]',
            optionsData: [
                {
                    id: 'single_entry',
                    text: 'Single Entry Type Module',
                },
                {
                    id: 'non_sortable',
                    text: 'Non Sortable Module',
                },
                {
                    id: 'sortable',
                    text: 'Sortable Module',
                },
                {
                    id: 'multilevel_sortable',
                    text: 'Multilevel Sortable Module',
                },
            ],
            preselectFirst: false,
            required: true,
            relatedTableName: false,
            tooltip:
                'Learn more about module types in the sidebar quick help section.',
            value: _.has(selectedModule, 'type') ? selectedModule.type : null,
            vueComponent: 'Relationship',
        },

        /**
         * Module name field
         */
        {
            id: 'name',
            label: 'Module Name',
            mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_NAME',
            name: 'module-name',
            placeholder: 'My Module Name',
            translatable: true,
            supportsTranslation: true,
            tooltip: {
                [ModuleConfigurator.ui.defaultLocale.languageCode]:
                    'Human-readable name of the module.',
            },
            value:
                _.has(selectedModule, 'name') && selectedModule.name
                    ? selectedModule.name
                    : null,
            vueComponent: 'InputText',
        },

        /**
         * Table name field
         */
        {
            disabled: !ModuleConfigurator.generator.newModule,
            id: 'table_name',
            label: 'Table Name',
            mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_TABLE_NAME',
            name: 'module-table_name',
            placeholder: 'my_table_name',
            tooltip:
                'Auto-generated table name for the module in snake_case notation.',
            value:
                _.has(selectedModule, 'table_name') && selectedModule.table_name
                    ? selectedModule.table_name
                    : null,
            vueComponent: 'InputText',
        },

        /**
         * Anchor text field
         */
        {
            id: 'anchor_text',
            label: 'Anchor Text',
            mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_ANCHOR_TEXT',
            name: 'module[anchor_text]',
            placeholder: '{{system.id}} - {{fields.field_name}}',
            tooltip:
                'Stub which will be used to generate human-readable text for each entry representation.',
            value: _.has(selectedModule, 'anchor_text')
                ? selectedModule.anchor_text
                : null,
            vueComponent: 'InputText',
        },

        /**
         * Anchor text field
         */
        {
            id: 'content_preview',
            label: 'Content Preview',
            mutation:
                'generator/UPDATE_GENERATOR_SELECTED_MODULE_CONTENT_PREVIEW',
            name: 'module[content_preview]',
            placeholder: 'https://testsite.com/test/{{system.id}}',
            tooltip: 'Generate preview button link',
            value: _.has(selectedModule, 'content_preview')
                ? selectedModule.content_preview
                : null,
            vueComponent: 'InputText',
        },

        /**
         * Anchor html field
         */
        {
            id: 'anchor_html',
            label: 'Anchor HTML',
            mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_ANCHOR_HTML',
            name: 'module[anchor_html]',
            placeholder:
                '{{system.id}} - <strong>{{fields.field_name}}</strong>',
            tooltip:
                'Stub which will be used to generate HTML snippet for each entry representation.',
            value: _.has(selectedModule, 'anchor_html')
                ? selectedModule.anchor_html
                : null,
            vueComponent: 'InputText',
        },

        /**
         * Slug field
         */
        {
            id: 'slug',
            label: 'Slug',
            mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_SLUG',
            name: 'module[slug]',
            placeholder: '{{field.field_name}}-{{fields.another_field_name}}',
            tooltip:
                'Stub which will be used to generate URL-friendly text for each entry representation (similar to Anchor Text).',
            value: _.has(selectedModule, 'slug') ? selectedModule.slug : null,
            vueComponent: 'InputText',
        },

        /**
         * Module icon field
         */
        {
            id: 'icon',
            label: 'Icon',
            mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_ICON',
            name: 'module[icon]',
            // optionsData: iconOptionsSelect2,
            // preselectFirst: true,
            tooltip:
                'Generic icon name used in Photon CMS administration panel interface.',
            value: _.has(selectedModule, 'icon') ? selectedModule.icon : null,
            vueComponent: 'BootstrapIconPicker',
        },

        /**
         * Parent module field
         */
        {
            disabled: !ModuleConfigurator.generator.newModule,
            id: 'category',
            label: 'Parent Module',
            mutation: 'generator/UPDATE_GENERATOR_SELECTED_MODULE_CATEGORY',
            name: 'module[category]',
            optionsData: _mapCategoryModulesSelect2(
                ModuleConfigurator.categoryModules,
                ModuleConfigurator.ui
            ),
            placeholder: 'Select parent module',
            preselectFirst: false,
            required: false,
            relatedTableName: false,
            tooltip:
                'Module ID of a parent module. If this value is set then each entry of the current module can belong to an entry of the parent module (scope).',
            value:
                _.has(selectedModule, 'category') && selectedModule.category
                    ? selectedModule.category
                    : '0',
            vueComponent: 'Relationship',
        },

        /**
         * Enable export
         */
        {
            defaultValue: true,
            id: 'enable_export',
            label: 'Enable export',
            mutation: 'generator/UPDATE_GENERATOR_ENABLE_EXPORT',
            name: 'module[enable_export]',
            tooltip: 'If checked, enables export data option for this module.',
            value: _.has(selectedModule, 'enable_export')
                ? selectedModule.enable_export
                : false,
            vueComponent: 'Boolean',
        },

        /**
         * Create factory field
         */
        {
            defaultValue: true,
            id: 'create_factory',
            label: 'Create factory',
            mutation: 'generator/UPDATE_GENERATOR_CREATE_FACTORY',
            name: 'module[create_factory]',
            tooltip: 'If checked, creates a seed factory for this module.',
            value: _.has(selectedModule, 'create_factory')
                ? selectedModule.create_factory
                : false,
            vueComponent: 'Boolean',
        },

        /**
         * Factory number field
         */
        {
            id: 'factory_number',
            label: 'Number of seeded records',
            mutation: 'generator/UPDATE_GENERATOR_FACTORY_NUMBER',
            name: 'module[factory_number]',
            tooltip:
                'A number of records generated by a faker-driven seed factory.',
            value: _.has(selectedModule, 'factory_number')
                ? selectedModule.factory_number
                : null,
            vueComponent: 'InputText',
        },

        /**
         * Publish module
         */
        {
            defaultValue: false,
            id: 'published',
            label: 'Publish module',
            mutation: 'generator/UPDATE_GENERATOR_PUBLISHED',
            name: 'module[published]',
            tooltip:
                'If checked, the public api route for this module will be available.',
            value: _.has(selectedModule, 'published')
                ? selectedModule.published
                : false,
            vueComponent: 'Boolean',
        },

        /**
         * Publish id field
         */
        {
            defaultValue: false,
            id: 'publish_id',
            label: 'Publish ID',
            mutation: 'generator/UPDATE_GENERATOR_PUBLISH_ID',
            name: 'module[publish_id]',
            tooltip:
                'If checked, id field will be available in the public api route response.',
            value: _.has(selectedModule, 'publish_id')
                ? selectedModule.publish_id
                : false,
            vueComponent: 'Boolean',
        },

        /**
         * Publish slug field
         */
        {
            defaultValue: false,
            id: 'publish_slug',
            label: 'Publish slug',
            mutation: 'generator/UPDATE_GENERATOR_PUBLISH_SLUG',
            name: 'module[publish_slug]',
            tooltip:
                'If checked, slug field will be available in the public api route response.',
            value: _.has(selectedModule, 'publish_slug')
                ? selectedModule.publish_slug
                : false,
            vueComponent: 'Boolean',
        },

        /**
         * Publish created_by and updated_by fields
         */
        {
            defaultValue: false,
            id: 'publish_ownership',
            label: 'Publish ownership',
            mutation: 'generator/UPDATE_GENERATOR_PUBLISH_OWNERSHIP',
            name: 'module[publish_ownership]',
            tooltip:
                'If checked, created_by and updated_by fields will be available in the public api route response.',
            value: _.has(selectedModule, 'publish_ownership')
                ? selectedModule.publish_ownership
                : false,
            vueComponent: 'Boolean',
        },

        /**
         * Publish created_at and updated_at fields
         */
        {
            defaultValue: false,
            id: 'publish_timestamps',
            label: 'Publish timestamps',
            mutation: 'generator/UPDATE_GENERATOR_PUBLISH_TIMESTAMPS',
            name: 'module[publish_timestamps]',
            tooltip:
                'If checked, created_at and updated_at fields will be available in the public api route response.',
            value: _.has(selectedModule, 'publish_timestamps')
                ? selectedModule.publish_timestamps
                : false,
            vueComponent: 'Boolean',
        },
    ];

    return configuration;
};
